import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { useHealth } from '@/contexts/HealthContext';
import { useReproduction } from '@/contexts/ReproductionContext';
import { 
  ArrowLeft, 
  Calendar, 
  TrendingUp,
  Activity,
  FileText,
  AlertCircle,
  Edit,
  Trash2,
  MapPin,
  Heart,
  Plus,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import PregnancyDialog from '@/components/ganado/PregnancyDialog';
import CheckupDialog from '@/components/ganado/CheckupDialog';
import PalpationDialog from '@/components/ganado/PalpationDialog';
import PregnancyStatusDialog from '@/components/ganado/PregnancyStatusDialog';
import LinkOffspringDialog from '@/components/ganado/LinkOffspringDialog';
import { toast } from 'sonner';

export default function CattleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    cattle, 
    weightRecords, 
    getMother, 
    getFather, 
    getOffspring, 
    getAllDescendants,
    updateCattle,
    isRelated,
    linkMother,
    linkFather
  } = useCattle();
  const { diagnoses, treatments } = useHealth();
  const { 
    getPregnanciesByCattle, 
    getActivePregnancy,
    getPregnanciesAsFather,
    addPregnancy, 
    addCheckup,
    addPalpation,
    completePregnancy, 
    abortPregnancy 
  } = useReproduction();
  
  const [activeTab, setActiveTab] = useState('peso');
  const [showPregnancyDialog, setShowPregnancyDialog] = useState(false);
  const [showCheckupDialog, setShowCheckupDialog] = useState(false);
  const [showPalpationDialog, setShowPalpationDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showLinkOffspringDialog, setShowLinkOffspringDialog] = useState(false);
  const [selectedPregnancy, setSelectedPregnancy] = useState(null);

  const animal = cattle?.find(c => c.id === id);
  const animalWeights = (weightRecords || [])
    .filter(w => w.cattleId === id)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const animalDiagnoses = (diagnoses || []).filter(d => d.cattleId === id);
  const animalTreatments = (treatments || []).filter(t => t.cattleId === id);
  
  // Reproduction data
  const animalPregnancies = getPregnanciesByCattle(id);
  const activePregnancy = getActivePregnancy(id);
  const pregnanciesAsFather = getPregnanciesAsFather(id);
  const bulls = cattle?.filter(c => c.gender === 'Macho') || [];

  // Genealogy data
  const mother = getMother(id);
  const father = getFather(id);
  const offspring = getOffspring(id);
  const allDescendants = getAllDescendants(id);
  const totalDescendants = allDescendants.length;

  if (!animal) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Animal no encontrado</h2>
          <p className="text-muted-foreground mb-4">No se pudo encontrar el animal solicitado</p>
          <Button onClick={() => navigate('/ganaderia/inventario')}>
            Volver al Inventario
          </Button>
        </div>
      </div>
    );
  }

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const birth = new Date(birthDate);
    const today = new Date();
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return `${years} año${years !== 1 ? 's' : ''} ${remainingMonths} mes${remainingMonths !== 1 ? 'es' : ''}`;
  };

  const getLatestWeight = () => {
    if (animalWeights.length === 0) return 'N/A';
    return `${animalWeights[animalWeights.length - 1].weight} kg`;
  };

  const getTotalWeightGain = () => {
    if (animalWeights.length < 2) return '+0.0 kg';
    const first = animalWeights[0].weight;
    const last = animalWeights[animalWeights.length - 1].weight;
    const gain = last - first;
    return `${gain >= 0 ? '+' : ''}${gain.toFixed(1)} kg`;
  };

  const getAverageDailyGain = () => {
    if (animalWeights.length < 2) return '0.00';
    const first = animalWeights[0];
    const last = animalWeights[animalWeights.length - 1];
    const weightDiff = last.weight - first.weight;
    const daysDiff = Math.abs(new Date(last.date) - new Date(first.date)) / (1000 * 60 * 60 * 24);
    return daysDiff > 0 ? (weightDiff / daysDiff).toFixed(2) : '0.00';
  };

  const getDaysTracked = () => {
    if (animalWeights.length < 2) return 0;
    const first = new Date(animalWeights[0].date);
    const last = new Date(animalWeights[animalWeights.length - 1].date);
    return Math.floor(Math.abs(last - first) / (1000 * 60 * 60 * 24));
  };

  const weightChartData = animalWeights.map(w => ({
    date: new Date(w.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    peso: w.weight,
    fullDate: w.date
  }));

  const statusColors = {
    'Activo': 'bg-green-100 text-green-800 border-green-200',
    'En Tratamiento': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Vendido': 'bg-blue-100 text-blue-800 border-blue-200',
    'Fallecido': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  // Reproduction handlers
  const handleSavePregnancy = (pregnancyData) => {
    addPregnancy(pregnancyData);
    toast.success('Embarazo registrado exitosamente');
    setShowPregnancyDialog(false);
  };

  const handleAddCheckup = (checkupData) => {
    addCheckup(selectedPregnancy.id, checkupData);
    setShowCheckupDialog(false);
  };

  const handleAddPalpation = (palpationData) => {
    addPalpation(selectedPregnancy.id, palpationData);
    setShowPalpationDialog(false);
  };

  const handleCompletePregnancy = (birthData) => {
    completePregnancy(selectedPregnancy.id, birthData);
    setShowStatusDialog(false);
  };

  const handleAbortPregnancy = (abortionData) => {
    abortPregnancy(selectedPregnancy.id, abortionData);
    setShowStatusDialog(false);
  };

  const handleLinkOffspring = async (offspringId) => {
    if (animal.gender === 'Hembra') {
      const result = await linkMother(offspringId, animal.id);
      if (result.success) {
        toast.success('Cría vinculada exitosamente como hija');
      } else {
        toast.error('Error al vincular cría');
      }
    } else {
      const result = await linkFather(offspringId, animal.id);
      if (result.success) {
        toast.success('Cría vinculada exitosamente como hijo');
      } else {
        toast.error('Error al vincular cría');
      }
    }
  };

  const getDaysPregnant = (conceptionDate) => {
    return Math.floor((new Date() - new Date(conceptionDate)) / (1000 * 60 * 60 * 24));
  };

  const getFatherInfo = (pregnancy) => {
    if (pregnancy.reproductionType === 'natural' && pregnancy.fatherId) {
      const father = cattle?.find(c => c.id === pregnancy.fatherId);
      return father ? `${father.name} (${father.tag})` : 'Toro no encontrado';
    }
    return 'Inseminación Artificial';
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-[1600px]">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => navigate('/ganaderia/inventario')}
        className="mb-6 hover:bg-gray-100"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver al Inventario
      </Button>

      {/* 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Animal Info */}
        <div className="lg:col-span-3">
          <Card className="border-border/50">
            <CardContent className="p-0">
              {/* Animal Image */}
              <div className="relative h-64 bg-gradient-to-br from-muted/50 to-muted">
                {animal.imageUrl ? (
                  <img
                    src={animal.imageUrl}
                    alt={animal.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Activity className="h-16 w-16 text-muted-foreground/30" />
                  </div>
                )}
              </div>

              {/* Basic Info */}
              <div className="p-6 space-y-4">
                <div>
                  <h2 className="text-2xl font-bold">{animal.name}</h2>
                  <p className="text-sm text-muted-foreground">{animal.tag}</p>
                </div>

                <Badge variant="outline" className={statusColors[animal.status] || 'bg-gray-100'}>
                  {animal.status}
                </Badge>

                <div className="space-y-3 pt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Raza</p>
                    <p className="text-sm font-medium">{animal.breed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Edad</p>
                    <p className="text-sm font-medium">{calculateAge(animal.birthDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Género</p>
                    <p className="text-sm font-medium">{animal.gender === 'Macho' ? 'Macho' : 'Hembra'}</p>
                  </div>
                  {animal.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <p className="text-sm font-medium">{animal.location}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 pt-4">
                  <Button variant="outline" size="sm" className="w-full">
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center Column - Tabs and Charts */}
        <div className="lg:col-span-6">
          <Card className="border-border/50">
            <CardContent className="p-6">
              {/* Tab Buttons */}
              <div className="flex gap-2 mb-6 border-b">
                <button
                  onClick={() => setActiveTab('peso')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    activeTab === 'peso'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  Peso
                </button>
                <button
                  onClick={() => setActiveTab('salud')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    activeTab === 'salud'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Activity className="h-4 w-4" />
                  Salud
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    activeTab === 'info'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Info
                </button>
                {animal.gender === 'Hembra' && (
                  <button
                    onClick={() => setActiveTab('reproduccion')}
                    className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                      activeTab === 'reproduccion'
                        ? 'border-rose-600 text-rose-600'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Heart className="h-4 w-4" />
                    Reproducción
                  </button>
                )}
              </div>

              {/* Tab Content */}
              {activeTab === 'peso' && (
                <div className="space-y-6">
                  {/* Weight Chart */}
                  {weightChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={weightChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                          labelStyle={{ color: '#374151' }}
                        />
                        <Line type="monotone" dataKey="peso" stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No hay registros de peso disponibles</p>
                  )}

                  {/* Weight Records */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Registros de Peso</h3>
                    <div className="space-y-3">
                      {animalWeights.slice().reverse().map((record, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Activity className="h-5 w-5 text-rose-600" />
                            <div>
                              <p className="font-semibold">{record.weight} kg</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(record.date).toLocaleDateString('es-ES', { 
                                  year: 'numeric', 
                                  month: '2-digit',
                                  day: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="bg-gray-100 text-gray-700">
                              {record.method || 'Bascula'}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {record.notes || (idx === animalWeights.length - 1 ? 'Peso inicial' : 'Progreso normal')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'salud' && (
                <div className="space-y-6">
                  {/* Diagnoses */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Diagnósticos</h3>
                    {animalDiagnoses.length > 0 ? (
                      <div className="space-y-3">
                        {animalDiagnoses.map((diagnosis, idx) => (
                          <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium">{diagnosis.diagnosis}</h4>
                              <Badge variant="outline" className={
                                diagnosis.severity === 'Alta' ? 'bg-red-100 text-red-800' :
                                diagnosis.severity === 'Media' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }>
                                {diagnosis.severity}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{diagnosis.symptoms}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>Dr. {diagnosis.veterinarian}</span>
                              <span>•</span>
                              <span>{new Date(diagnosis.date).toLocaleDateString('es-ES')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No hay diagnósticos registrados</p>
                    )}
                  </div>

                  {/* Treatments */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Tratamientos</h3>
                    {animalTreatments.length > 0 ? (
                      <div className="space-y-3">
                        {animalTreatments.map((treatment, idx) => (
                          <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium">{treatment.treatment}</h4>
                              <Badge variant="outline" className={
                                treatment.status === 'Completado' ? 'bg-green-100 text-green-800' :
                                treatment.status === 'En Progreso' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {treatment.status}
                              </Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-muted-foreground">Medicamento:</span> {treatment.medication}</p>
                              <p><span className="text-muted-foreground">Dosis:</span> {treatment.dosage}</p>
                              <p><span className="text-muted-foreground">Frecuencia:</span> {treatment.frequency}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No hay tratamientos registrados</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'info' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Nombre</p>
                      <p className="text-sm font-medium">{animal.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Tag</p>
                      <p className="text-sm font-medium">{animal.tag}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Raza</p>
                      <p className="text-sm font-medium">{animal.breed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Género</p>
                      <p className="text-sm font-medium">{animal.gender === 'Macho' ? 'Macho' : 'Hembra'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Fecha de Nacimiento</p>
                      <p className="text-sm font-medium">{animal.birthDate ? new Date(animal.birthDate).toLocaleDateString('es-ES') : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Estado</p>
                      <p className="text-sm font-medium">{animal.status}</p>
                    </div>
                    {animal.purchaseDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">Fecha de Compra</p>
                        <p className="text-sm font-medium">{new Date(animal.purchaseDate).toLocaleDateString('es-ES')}</p>
                      </div>
                    )}
                    {animal.location && (
                      <div>
                        <p className="text-xs text-muted-foreground">Ubicación</p>
                        <p className="text-sm font-medium">{animal.location}</p>
                      </div>
                    )}
                  </div>

                  {/* Registry Section */}
                  <div className="pt-6 border-t">
                    <h4 className="text-sm font-semibold mb-3">Registro Genealógico</h4>
                    {animal.hasRegistry ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-500 text-white">
                            ⭐ Animal con Registro
                          </Badge>
                        </div>
                        {animal.registryNumber && (
                          <div>
                            <p className="text-xs text-muted-foreground">Número de Registro</p>
                            <p className="text-sm font-medium font-mono">{animal.registryNumber}</p>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Este animal cuenta con registro genealógico oficial. Los cruces con otros animales registrados producirán crías puras.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Este animal no tiene registro genealógico
                      </p>
                    )}
                  </div>

                  {/* Genealogy Section */}
                  <div className="pt-6 border-t">
                    <h4 className="text-sm font-semibold mb-3">Genealogía</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Madre</p>
                        {mother ? (
                          <button
                            onClick={() => navigate(`/ganaderia/detalle/${mother.id}`)}
                            className="text-sm font-medium text-rose-600 hover:underline"
                          >
                            {mother.name} ({mother.tag})
                          </button>
                        ) : (
                          <p className="text-sm text-muted-foreground">No registrada</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Padre</p>
                        {father ? (
                          <button
                            onClick={() => navigate(`/ganaderia/detalle/${father.id}`)}
                            className="text-sm font-medium text-rose-600 hover:underline"
                          >
                            {father.name} ({father.tag})
                          </button>
                        ) : (
                          <p className="text-sm text-muted-foreground">No registrado</p>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-muted-foreground">Crías Directas ({offspring.length})</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowLinkOffspringDialog(true)}
                            className="text-xs h-7"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Agregar Cría
                          </Button>
                        </div>
                        {offspring.length > 0 ? (
                          <div className="space-y-1">
                            {offspring.map(child => (
                              <button
                                key={child.id}
                                onClick={() => navigate(`/ganaderia/detalle/${child.id}`)}
                                className="block text-sm text-rose-600 hover:underline"
                              >
                                {child.name} ({child.tag})
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No hay crías registradas</p>
                        )}
                      </div>
                      {totalDescendants > 0 && (
                        <div className="bg-rose-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-rose-900">
                            Total de Descendientes: {totalDescendants}
                          </p>
                          <p className="text-xs text-rose-700 mt-1">
                            Incluye hijos, nietos, bisnietos, etc.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {animal.notes && (
                    <div className="pt-4">
                      <p className="text-xs text-muted-foreground mb-2">Notas</p>
                      <p className="text-sm whitespace-pre-wrap bg-muted/30 p-3 rounded-lg">{animal.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reproduccion' && animal.gender === 'Hembra' && (
                <div className="space-y-6">
                  {/* Embarazo Activo */}
                  {activePregnancy && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-rose-900 flex items-center gap-2">
                              <Heart className="h-5 w-5 text-rose-600" />
                              Embarazo Activo
                            </h3>
                            {activePregnancy.isPureBreed && (
                              <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0">
                                ⭐ Cría Pura
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-rose-700">
                            Días de gestación: {getDaysPregnant(activePregnancy.conceptionDate)} días
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPregnancy(activePregnancy);
                              setShowCheckupDialog(true);
                            }}
                            className="border-rose-300 text-rose-700 hover:bg-rose-100"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Revisión
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPregnancy(activePregnancy);
                              setShowPalpationDialog(true);
                            }}
                            className="border-rose-300 text-rose-700 hover:bg-rose-100"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Palpación
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPregnancy(activePregnancy);
                              setShowStatusDialog(true);
                            }}
                            className="bg-rose-600 hover:bg-rose-700"
                          >
                            Actualizar Estado
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-rose-700">Fecha de Concepción</p>
                          <p className="text-sm font-medium text-rose-900">
                            {new Date(activePregnancy.conceptionDate).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-rose-700">Fecha Esperada de Parto</p>
                          <p className="text-sm font-medium text-rose-900">
                            {new Date(activePregnancy.expectedDate).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-rose-700">Tipo de Reproducción</p>
                          <p className="text-sm font-medium text-rose-900">
                            {activePregnancy.reproductionType === 'natural' ? 'Monta Natural' : 'Inseminación Artificial'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-rose-700">Padre</p>
                          <p className="text-sm font-medium text-rose-900">
                            {getFatherInfo(activePregnancy)}
                          </p>
                        </div>
                      </div>

                      {activePregnancy.checkups && activePregnancy.checkups.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-rose-900 mb-2">Revisiones del Embarazo</h4>
                          <div className="space-y-2">
                            {activePregnancy.checkups.slice().reverse().map((checkup, idx) => (
                              <div key={idx} className="bg-white p-3 rounded border border-rose-200">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge variant="outline" className={
                                        checkup.status === 'good' ? 'bg-green-100 text-green-800' :
                                        checkup.status === 'concern' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }>
                                        {checkup.status === 'good' ? 'Normal' : checkup.status === 'concern' ? 'Preocupante' : 'Crítico'}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(checkup.date).toLocaleDateString('es-ES')}
                                      </span>
                                    </div>
                                    <p className="text-sm">{checkup.notes}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Dr. {checkup.veterinarian}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {activePregnancy.palpations && activePregnancy.palpations.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-rose-900 mb-2">Palpaciones Registradas</h4>
                          <div className="space-y-2">
                            {activePregnancy.palpations.slice().reverse().map((palpation, idx) => (
                              <div key={idx} className="bg-white p-3 rounded border border-rose-200">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className={
                                      palpation.fetusDetected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }>
                                      {palpation.fetusDetected ? 'Feto Detectado' : 'No Detectado'}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(palpation.date).toLocaleDateString('es-ES')} - Día {palpation.daysSinceConception}
                                    </span>
                                  </div>
                                </div>
                                {palpation.fetusDetected && (
                                  <div className="space-y-1 text-sm">
                                    {palpation.heartbeat !== null && (
                                      <p><span className="text-muted-foreground">Latido:</span> {palpation.heartbeat ? 'Sí' : 'No'}</p>
                                    )}
                                    <p><span className="text-muted-foreground">Tamaño:</span> {
                                      palpation.fetusSize === 'small' ? 'Pequeño' :
                                      palpation.fetusSize === 'large' ? 'Grande' : 'Normal'
                                    }</p>
                                    <p><span className="text-muted-foreground">Posición:</span> {
                                      palpation.position === 'abnormal' ? 'Anormal' : 'Normal'
                                    }</p>
                                  </div>
                                )}
                                {palpation.notes && <p className="text-sm mt-1">{palpation.notes}</p>}
                                <p className="text-xs text-muted-foreground mt-1">Dr. {palpation.veterinarian}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botón para registrar nuevo embarazo */}
                  {!activePregnancy && (
                    <Button
                      onClick={() => setShowPregnancyDialog(true)}
                      className="w-full bg-rose-600 hover:bg-rose-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Registrar Nuevo Embarazo
                    </Button>
                  )}

                  {/* Historial de Embarazos */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Historial de Embarazos</h3>
                    {animalPregnancies.length > 0 ? (
                      <div className="space-y-3">
                        {animalPregnancies.map((pregnancy, idx) => (
                          <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={
                                  pregnancy.status === 'active' ? 'bg-rose-100 text-rose-800' :
                                  pregnancy.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {pregnancy.status === 'active' ? 'Activo' : 
                                   pregnancy.status === 'completed' ? 'Completado' : 'Abortado'}
                                </Badge>
                                {pregnancy.isPureBreed && (
                                  <Badge className="bg-amber-500 text-white text-xs">
                                    ⭐ Pura
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right text-sm text-muted-foreground">
                                {new Date(pregnancy.conceptionDate).toLocaleDateString('es-ES')}
                              </div>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p><span className="text-muted-foreground">Padre:</span> {getFatherInfo(pregnancy)}</p>
                              <p><span className="text-muted-foreground">Revisiones:</span> {pregnancy.checkups?.length || 0}</p>
                              {pregnancy.birth && (
                                <p className="text-green-700">
                                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                  Parto: {new Date(pregnancy.birth.date).toLocaleDateString('es-ES')}
                                </p>
                              )}
                              {pregnancy.status === 'aborted' && (
                                <p className="text-orange-700">
                                  <XCircle className="h-3 w-3 inline mr-1" />
                                  Aborto: {new Date(pregnancy.abortionDate).toLocaleDateString('es-ES')}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No hay embarazos registrados</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Stats and Actions */}
        <div className="lg:col-span-3 space-y-6">
          {/* Weight Statistics */}
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Estadísticas de Peso
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground">Ganancia Total</p>
                <p className="text-2xl font-bold text-rose-600">{getTotalWeightGain()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ganancia Promedio Diaria</p>
                <p className="text-xl font-semibold">{getAverageDailyGain()} kg/día</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Días Rastreados</p>
                <p className="text-xl font-semibold">{getDaysTracked()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Registros de Peso</p>
                <p className="text-xl font-semibold">{animalWeights.length}</p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" size="sm">
                <TrendingUp className="h-4 w-4 mr-2" />
                Registrar Peso
              </Button>
              <Button variant="outline" className="w-full justify-start" size="sm">
                <Calendar className="h-4 w-4 mr-2" />
                Ver Historial
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <PregnancyDialog
        open={showPregnancyDialog}
        onOpenChange={setShowPregnancyDialog}
        cattle={animal}
        bulls={bulls}
        onSave={handleSavePregnancy}
        checkRelation={(bullId) => isRelated(animal.id, bullId)}
      />

      {selectedPregnancy && (
        <>
          <CheckupDialog
            open={showCheckupDialog}
            onOpenChange={setShowCheckupDialog}
            pregnancy={selectedPregnancy}
            onSave={handleAddCheckup}
          />

          <PalpationDialog
            open={showPalpationDialog}
            onOpenChange={setShowPalpationDialog}
            pregnancy={selectedPregnancy}
            onSave={handleAddPalpation}
          />

          <PregnancyStatusDialog
            open={showStatusDialog}
            onOpenChange={setShowStatusDialog}
            pregnancy={selectedPregnancy}
            allCattle={cattle}
            onComplete={handleCompletePregnancy}
            onAbort={handleAbortPregnancy}
          />
        </>
      )}

      <LinkOffspringDialog
        open={showLinkOffspringDialog}
        onOpenChange={setShowLinkOffspringDialog}
        cattle={animal}
        allCattle={cattle}
        onLink={handleLinkOffspring}
      />
    </div>
  );
}
